import { forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "danger"
    | "ghost"
    | "link"
    | "success"
    | "outline";
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "icon";
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      loading,
      children,
      disabled,
      icon,
      iconPosition = "left",
      ...props
    },
    ref,
  ) => {
    const baseStyles = cn(
      // Base
      "inline-flex items-center justify-center gap-2 font-medium",
      // Rounded
      "rounded-xl",
      // Transitions
      "transition-all duration-200 ease-out",
      // Focus
      "focus:outline-none focus:ring-2 focus:ring-offset-2",
      // Disabled
      "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none",
      // Active
      "active:scale-[0.98]",
    );

    const variants = {
      primary: cn(
        "bg-gradient-to-r from-primary-600 to-primary-500 text-white",
        "hover:from-primary-700 hover:to-primary-600",
        "focus:ring-primary-500",
        "shadow-md hover:shadow-lg hover:shadow-primary-500/25",
      ),
      secondary: cn(
        "bg-white text-gray-700 border border-gray-200",
        "hover:bg-gray-50 hover:border-gray-300",
        "focus:ring-gray-400",
        "shadow-sm",
      ),
      outline: cn(
        "bg-transparent text-primary-600 border-2 border-primary-500",
        "hover:bg-primary-50",
        "focus:ring-primary-500",
      ),
      danger: cn(
        "bg-gradient-to-r from-red-600 to-red-500 text-white",
        "hover:from-red-700 hover:to-red-600",
        "focus:ring-red-500",
        "shadow-md hover:shadow-lg hover:shadow-red-500/25",
      ),
      success: cn(
        "bg-gradient-to-r from-emerald-600 to-emerald-500 text-white",
        "hover:from-emerald-700 hover:to-emerald-600",
        "focus:ring-emerald-500",
        "shadow-md hover:shadow-lg hover:shadow-emerald-500/25",
      ),
      ghost: cn(
        "bg-transparent text-gray-600",
        "hover:bg-gray-100 hover:text-gray-900",
        "focus:ring-gray-400",
      ),
      link: cn(
        "bg-transparent text-primary-600 px-0",
        "hover:text-primary-700 hover:underline underline-offset-4",
        "focus:ring-0",
      ),
    };

    const sizes = {
      xs: "px-2.5 py-1 text-xs",
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2 text-sm",
      lg: "px-5 py-2.5 text-base",
      xl: "px-6 py-3 text-base",
      icon: "p-2",
    };

    const iconSizes = {
      xs: "w-3 h-3",
      sm: "w-3.5 h-3.5",
      md: "w-4 h-4",
      lg: "w-5 h-5",
      xl: "w-5 h-5",
      icon: "w-5 h-5",
    };

    const Spinner = () => (
      <svg
        className={cn("animate-spin", iconSizes[size])}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    const IconWrapper = ({
      children: iconChild,
    }: {
      children: React.ReactNode;
    }) => (
      <span className={cn(iconSizes[size], "flex items-center justify-center")}>
        {iconChild}
      </span>
    );

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || loading}
        aria-busy={loading}
        aria-disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <Spinner />
        ) : (
          icon && iconPosition === "left" && <IconWrapper>{icon}</IconWrapper>
        )}
        {children}
        {!loading && icon && iconPosition === "right" && (
          <IconWrapper>{icon}</IconWrapper>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
